import Interaction from "../models/interactions/Interaction";
import { IFormQuiz } from "../models/Quiz";
import Take from "../models/Take";
import { Training } from "../models/Training";

const isEnabled = (interaction: Interaction) => interaction.disabled !== true;

const hasDisabledFeedback = (interaction: Interaction): boolean => {
  const quiz = interaction as IFormQuiz;
  return Boolean(
    quiz.correctFeedbackInteractions?.some((i) => !isEnabled(i)) ||
      quiz.wrongFeedbackInteractions?.some((i) => !isEnabled(i))
  );
};

const stripFeedbackSlots = (interaction: Interaction): Interaction => {
  if (!hasDisabledFeedback(interaction)) return interaction;
  const quiz = interaction as IFormQuiz;
  const stripped: IFormQuiz = {
    ...quiz,
    correctFeedbackInteractions:
      quiz.correctFeedbackInteractions?.filter(isEnabled),
    wrongFeedbackInteractions:
      quiz.wrongFeedbackInteractions?.filter(isEnabled),
  };
  return stripped;
};

const stripTake = (take: Take): Take => {
  const needsStrip = take.interactions.some(
    (interaction) => !isEnabled(interaction) || hasDisabledFeedback(interaction)
  );
  if (!needsStrip) return take;
  return {
    ...take,
    interactions: take.interactions.filter(isEnabled).map(stripFeedbackSlots),
  };
};

/**
 * Counts a take's disabled interactions, including those nested in quiz
 * feedback slots — everything the take-header badge should report as
 * excluded from testing.
 */
export function countDisabledInteractions(take: Take): number {
  return take.interactions.reduce((count, interaction) => {
    const quiz = interaction as IFormQuiz;
    const feedback = [
      ...(quiz.correctFeedbackInteractions ?? []),
      ...(quiz.wrongFeedbackInteractions ?? []),
    ];
    return (
      count +
      (isEnabled(interaction) ? 0 : 1) +
      feedback.filter((nested) => !isEnabled(nested)).length
    );
  }, 0);
}

/**
 * Returns a Training with disabled interactions removed from every take's
 * top-level list and from quiz feedback slots. Used for test payloads only —
 * saves keep disabled interactions. Pure: never mutates the input (take
 * arrays alias Redux state) and preserves take identity where nothing is
 * disabled. Sequence graphs are intentionally untouched: sequence-node
 * interactions cannot be disabled.
 */
export function stripDisabledInteractions<T extends Training | undefined>(
  training: T
): T {
  if (!training) return training;
  return {
    ...training,
    takes: (training.takes as Take[]).map(stripTake),
  } as T;
}
